import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Store } from '@ngrx/store';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import * as uuid from 'uuid';
import { FormsModule, NgForm } from '@angular/forms';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';
import { ILineItemSimple } from '@app/store/spend/spend.interfaces';
import { ProjectApiService } from '@app/services/project-api.service';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { FloatingInputComponent } from '@app/framework/inputs/floating-input/floating-input.component';
import { AddRemoveButtonsComponent } from '@app/framework/buttons/add-remove-buttons/add-remove-buttons.component';
import {
  AnticipatedIncome,
  AnticipatedIncomeModification,
} from '@app/framework/interfaces/anticipated-income.type';
import { NotificationsService } from '@app/services/notifications.service';
import { InteractionBarStateService } from '@app/services/interaction-bar-state.service';
import { GeneralSidebarHeaderComponent } from '@app/framework/general-sidebar-header/general-sidebar-header.component';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { BorderedDropdownIconComponent } from '@app/framework/bordered-dropdown-icon/bordered-dropdown-icon.component';
import { INTERACTION_BAR_STATES } from '@app/framework/constants/interaction-bar.constants';
import { MANAGE_PROJECT_TABS } from '@app/framework/constants/view-project.constants';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';

@Component({
  selector: 'app-anticipated-income-sidebar',
  imports: [
    DropdownComponent,
    SidebarHeaderComponent,
    SimpleButtonComponent,
    FloatingInputComponent,
    AddRemoveButtonsComponent,
    NgClass,
    LoadingSpinnerComponent,
    FormsModule,
    GeneralSidebarHeaderComponent,
    MoneyPipe,
    BorderedDropdownIconComponent,
  ],
  templateUrl: './anticipated-income-sidebar.component.html',
  styleUrl: './anticipated-income-sidebar.component.scss',
})
export class AnticipatedIncomeSidebarComponent implements OnInit, OnDestroy {
  projectApiService = inject(ProjectApiService);
  store = inject(Store);
  notif = inject(NotificationsService);
  private readonly interactionBar = inject(InteractionBarStateService);

  projectId$ = this.store.select(viewProjectSelectors.selectProjectId);
  projectId: number;

  isLoading = signal(true);
  lineItems: WritableSignal<ILineItemSimple[]> = signal([]);
  anticipatedIncomes: WritableSignal<AnticipatedIncome[]> = signal([]);

  generalForm = viewChild<NgForm>('generalForm');

  isDestroyed$ = new Subject<boolean>();

  readonly total = computed(() =>
    this.anticipatedIncomes().reduce((acc, anticipatedIncome) => {
      const pccos = anticipatedIncome?.pccos ?? [];
      const modifications = anticipatedIncome?.modifications ?? [];
      const totalValue = [...modifications, ...pccos].reduce(
        (sum, modification) => sum + (Number(modification.value) || 0),
        0,
      );
      return acc + totalValue;
    }, 0),
  );

  readonly disabledOptionIds = computed(() => {
    const forecastMods = this.anticipatedIncomes();
    return new Set(
      forecastMods
        .map((anticipatedIncome) => anticipatedIncome.item_id)
        .filter((id) => id !== null),
    );
  });

  ngOnInit() {
    this.projectId$
      .pipe(
        takeUntil(this.isDestroyed$),
        tap((projectId) => {
          this.projectId = projectId;
        }),
        switchMap((projectId) =>
          forkJoin([
            this.projectApiService.getProjectLineItemsSimple(projectId, true),
            this.projectApiService.getAnticipatedIncomeItems(projectId),
          ]),
        ),
      )
      .subscribe({
        next: ([lineItems, anticipatedIncomes]) => {
          this.lineItems.set(lineItems);
          this.anticipatedIncomes.set(
            anticipatedIncomes.filter((ai) => ai.modifications.length > 0 || ai.pccos.length > 0),
          );
          this.isLoading.set(false);
        },
        error: (error) => {
          this.notif.showError('Failed to load anticipated incomes.');
          this.isLoading.set(false);
        },
      });
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  lineItemChange(lineItemId: number, index: number) {
    if (!this.anticipatedIncomes()?.[index]) {
      console.error('No anticipated income found at index', index);
      return;
    }
    const newIncomes = this.anticipatedIncomes().map((anticipatedIncome, i) => {
      if (i === index) {
        return {
          ...anticipatedIncome,
          item_id: lineItemId,
        };
      }
      return anticipatedIncome;
    });
    this.anticipatedIncomes.set(newIncomes);
  }

  removeAnticipatedIncome(index: number) {
    this.notif
      .showPopup(`Are you sure you want to clear income group ${index + 1}?`)
      .then((shouldDelete) => {
        if (!shouldDelete) return;
        const incomes = DeepCopyService.deepCopy(this.anticipatedIncomes()); // deep copy needed here to trigger computed field updates
        incomes.splice(index, 1);
        this.anticipatedIncomes.set(incomes);
      });
  }

  addAnticipatedIncome() {
    const newAnticipatedIncome: AnticipatedIncome = {
      item_id: uuid.v4(),
      project_id: this.projectId,
      modifications: [
        {
          id: uuid.v4(),
          note: '',
          value: null,
        },
      ],
      pccos: [],
    };
    this.anticipatedIncomes().push(newAnticipatedIncome);
  }

  save() {
    this.generalForm().form.markAllAsTouched();

    if (this.anticipatedIncomes().some((ai) => uuid.validate(ai.item_id))) {
      this.notif.showError('Please select a line item for all anticipated incomes.');
      return;
    }
    if (this.generalForm().form.invalid) {
      this.notif.showError('Please fill in all required fields.');
      return;
    }

    this.notif.showLoading('Saving anticipated incomes...');
    const groupedIncomes: Record<number, AnticipatedIncomeModification[]> =
      this.anticipatedIncomes().reduce((acc, anticipatedIncome) => {
        const lineItemId = anticipatedIncome.item_id;
        if (!acc[lineItemId]) {
          acc[lineItemId] = [];
        }
        // modifications could go here
        acc[lineItemId].push(...anticipatedIncome.modifications);
        return acc;
      }, {});

    const requestData = Object.entries(groupedIncomes).map(([lineItemId, modifications]) => {
      return {
        item_id: Number(lineItemId),
        modifications: modifications,
      };
    });

    const data = {
      anticipated_income_items: requestData,
    };

    this.projectApiService.saveAnticipatedIncomeItems(this.projectId, data).subscribe({
      next: () => {
        this.notif.showSuccess('Anticipated incomes saved successfully');
        setTimeout(() => {
          this.interactionBar.close();
        }, 1000);
      },
      error: (error) => {
        this.notif.showError(
          'Failed to save anticipated incomes: ' + (error?.error?.message ?? 'An error occurred.'),
        );
      },
    });
  }

  removeAnticipatedIncomeDescription(index: number, descriptionIndex: number) {
    const newIncomes = this.anticipatedIncomes().map((anticipatedIncome, i) => {
      if (i !== index) {
        return anticipatedIncome;
      }
      const newModifications = anticipatedIncome.modifications.filter(
        (modification, j) => j !== descriptionIndex,
      );
      return {
        ...anticipatedIncome,
        modifications: newModifications,
      };
    });

    this.anticipatedIncomes.set(newIncomes);
  }

  /**
   * Adds a new anticipated income description.
   * @param index The index of the anticipated income to which the description will be added.
   * @param descriptionIndex The index of the description after which the new description will be added. (Default is -1. This means the description will be added at the beginning.)
   */
  addAnticipatedIncomeDescription(index: number, descriptionIndex = -1) {
    const newIncomes = this.anticipatedIncomes().map((anticipatedIncome, i) => {
      if (i !== index) {
        return anticipatedIncome;
      }
      anticipatedIncome.modifications.splice(descriptionIndex + 1, 0, {
        id: uuid.v4(),
        note: '',
        value: null,
      });
      return {
        ...anticipatedIncome,
      };
    });

    this.anticipatedIncomes.set(newIncomes);
  }

  switchViewToManageProject() {
    this.interactionBar.openInteractionBar(INTERACTION_BAR_STATES.MANAGE_PROJECT, {
      tab: MANAGE_PROJECT_TABS.BUDGET_CASHFLOW,
    });
  }

  onValueChange() {
    // this is needed to trigger computed change detection
    this.anticipatedIncomes.update((anticipatedIncomes) => {
      return [...anticipatedIncomes];
    });
  }

  async clearAll() {
    const response = await this.notif.showPopup(
      'Are you sure you want to clear all anticipated incomes?',
    );
    if (response) {
      this.anticipatedIncomes.set(
        this.anticipatedIncomes()
          .map((income) => ({ ...income, modifications: [] }))
          .filter((income) => income.pccos && income.pccos.length > 0),
      );
    }
  }
}
